import { createSlice } from '@reduxjs/toolkit';
import { TaxState } from '../../../types/tax';
import { TAX_PAYMENT_PERIOD_TYPES_ENUM } from 'src/common/enums';
import {
  LicensesPerPeriodByType,
  PeriodsByType,
  PeriodType,
  TaxPaymentPeriodsState,
} from 'src/types/tax';

// Set the initial state values of the state object that this slice will manage
const INITIAL_TAX_PAYMENT_PERIODS: TaxPaymentPeriodsState = {
  data: {
    [TAX_PAYMENT_PERIOD_TYPES_ENUM.MONTHLY]: [],
    [TAX_PAYMENT_PERIOD_TYPES_ENUM.QUARTERLY]: [],
  },
  loading: false,
  error: undefined,
};

const INITIAL_STATE: TaxState = {
  taxPaymentPeriods: INITIAL_TAX_PAYMENT_PERIODS,

  isCalculateTaxInProgress: false,
  isCalculateTaxSuccess: false,
  calculatedTaxData: { details: [] }, // raw data returned from  /calculateMultiPropertyTot
  calculatedTaxErrorMsg: '',

  preSelectedPeriod: undefined,

  taxFlowData: {
    selectedLicenses: [],
    reportingMethod: undefined,
  },
  licensesPerPeriod: {
    [TAX_PAYMENT_PERIOD_TYPES_ENUM.MONTHLY]: {},
    [TAX_PAYMENT_PERIOD_TYPES_ENUM.QUARTERLY]: {},
  },
  selectedPeriodType: TAX_PAYMENT_PERIOD_TYPES_ENUM.MONTHLY,
};

// Define the slice and generate actions, reducers with createSlice
export const taxSlice = createSlice({
  name: 'tax',
  initialState: INITIAL_STATE,
  reducers: {
    loadLicensesForPeriodStarted: (
      state,
      action: { payload: { periodType: PeriodType; startDate: string } }
    ) => {
      const { periodType, startDate } = action.payload;
      state.licensesPerPeriod[periodType][startDate] = {
        data: [],
        loading: true,
        error: undefined,
      };
    },
    loadLicensesForPeriodFailure: (
      state,
      action: {
        payload: { periodType: PeriodType; startDate: string; error: string };
      }
    ) => {
      const { periodType, startDate, error } = action.payload;
      state.licensesPerPeriod[periodType][startDate] = {
        data: [],
        loading: false,
        error,
      };
    },
    addLicensesForPeriod: (state, action) => {
      const { defaultPeriodType, licenses, period } = action.payload;
      const key = (period.period_type ?? defaultPeriodType) as PeriodType;
      state.licensesPerPeriod[key][period.startDate] = {
        data: licenses,
        loading: false,
        error: undefined,
      };
    },
    taxPaymentPeriodsStarted: (state) => {
      state.taxPaymentPeriods = INITIAL_TAX_PAYMENT_PERIODS;
      state.licensesPerPeriod = {
        [TAX_PAYMENT_PERIOD_TYPES_ENUM.MONTHLY]: {},
        [TAX_PAYMENT_PERIOD_TYPES_ENUM.QUARTERLY]: {},
      };
      state.selectedPeriodType = TAX_PAYMENT_PERIOD_TYPES_ENUM.MONTHLY;
    },
    taxPaymentPeriodSucceeded: (
      state,
      action: {
        payload: {
          licensesPerPeriod: LicensesPerPeriodByType;
          paymentPeriods: PeriodsByType;
          selectedPeriod: PeriodType;
        };
      }
    ) => {
      const { licensesPerPeriod, paymentPeriods, selectedPeriod } =
        action.payload;
      state.taxPaymentPeriods = {
        ...INITIAL_TAX_PAYMENT_PERIODS,
        data: paymentPeriods,
      };
      state.licensesPerPeriod = licensesPerPeriod;
      state.selectedPeriodType = selectedPeriod;
    },
    taxPaymentPeriodFailure: (state, action) => {
      state.taxPaymentPeriods = {
        ...INITIAL_TAX_PAYMENT_PERIODS,
        error: action.payload,
      };
    },
    calculateTaxStarted: (state) => {
      state.isCalculateTaxInProgress = true;
      state.isCalculateTaxSuccess = false;
      state.calculatedTaxErrorMsg = '';
    },
    calculateTaxSucceeded: (state, action) => {
      const data = action.payload;
      state.isCalculateTaxInProgress = false;
      state.isCalculateTaxSuccess = true;
      state.calculatedTaxData = data;
      state.calculatedTaxErrorMsg = '';
    },
    calculateTaxFailure: (state, action) => {
      state.isCalculateTaxInProgress = false;
      state.isCalculateTaxSuccess = false;
      state.calculatedTaxData = { details: [] };
      state.calculatedTaxErrorMsg = action.payload;
    },
    resetCalculateTax: (state) => {
      state.isCalculateTaxInProgress = false;
      state.isCalculateTaxSuccess = false;
      state.calculatedTaxErrorMsg = '';
    },
    setPeriodType: (state, action) => {
      state.selectedPeriodType = action.payload;
    },
    resetTaxState: () => INITIAL_STATE,
    setPreSelectedTaxPeriod: (state, action) => {
      state.preSelectedPeriod = action.payload;
    },
    clearPreSelectedTaxPeriod: (state) => {
      state.preSelectedPeriod = undefined;
    },
    updateTaxFlowData: (state, action) => {
      state.taxFlowData = {
        ...state.taxFlowData,
        ...action.payload,
      };
    },
  },
});

// Export actions that were generated by createSlice
export const {
  loadLicensesForPeriodStarted,
  loadLicensesForPeriodFailure,
  addLicensesForPeriod,
  taxPaymentPeriodsStarted,
  taxPaymentPeriodSucceeded,
  taxPaymentPeriodFailure,
  calculateTaxStarted,
  calculateTaxSucceeded,
  calculateTaxFailure,
  resetCalculateTax,
  resetTaxState,
  setPeriodType,
  setPreSelectedTaxPeriod,
  clearPreSelectedTaxPeriod,
  updateTaxFlowData,
} = taxSlice.actions;

// Export combined reducer that was generated by createSlice
// (this is a single reducer which is a combination of all reducers in the slice... it basically puts them in a switch statement)
export default taxSlice.reducer;
